import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Stethoscope, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("login"); // login | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [newPass, setNewPass] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const doLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/");
    } catch (err) {
      setError(apiError(err, "Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  const doForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      toast.success("If the email exists, a reset token was generated (check server logs in demo).");
      setView("reset");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: token.trim(), password: newPass });
      toast.success("Password updated. Please login.");
      setView("login");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (em, pw) => { setEmail(em); setPassword(pw); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-bk-blue text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative flex items-center gap-3">
          <img src="/logo.png" alt="BK" className="h-12 w-12 rounded-xl" />
          <div>
            <p className="font-display font-extrabold text-xl">BK Spine Care</p>
            <p className="text-white/70 text-sm">Physiotherapy Clinic</p>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-display font-extrabold text-4xl leading-tight">Clinic & Home<br />Physiotherapy<br /><span className="text-bk-green bg-white/95 px-2 rounded">Management</span></h2>
          <p className="text-white/70 mt-4 max-w-sm">Patients, attendance, advance ledger, home visits, collections, deposits and monthly settlements — all in one place.</p>
        </div>
        <p className="relative text-white/50 text-xs">Ranchi, Jharkhand · India</p>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 bg-background">
        <Card className="w-full max-w-md p-8 shadow-lg border-border">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <img src="/logo.png" alt="BK" className="h-10 w-10 rounded-lg" />
            <div>
              <p className="font-display font-extrabold text-bk-blue">BK Spine Care</p>
              <p className="text-xs text-slate-400 -mt-0.5">Physiotherapy Clinic</p>
            </div>
          </div>

          {view === "login" && (
            <form onSubmit={doLogin} className="space-y-4">
              <div>
                <h1 className="font-display font-extrabold text-2xl text-slate-800">Welcome back</h1>
                <p className="text-sm text-slate-500 mt-1">Sign in to manage the clinic</p>
              </div>
              {error && <div className="text-sm text-bk-danger bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@bkspinecare.in" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
              </div>
              <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-bk-green hover:bg-bk-greenDark text-white">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
              </Button>
              <button type="button" onClick={() => setView("forgot")} className="text-sm text-bk-blue hover:underline w-full text-center">Forgot password?</button>

              <div className="pt-4 border-t">
                <p className="text-xs text-slate-400 mb-2 text-center">Demo accounts — tap to fill</p>
                <div className="grid grid-cols-3 gap-2">
                  <button type="button" onClick={() => quickFill("admin@bkspinecare.in", "Admin@123")} className="text-[11px] px-2 py-1.5 rounded-md border hover:bg-bk-lightGreen">Admin</button>
                  <button type="button" onClick={() => quickFill("reception@bkspinecare.in", "Reception@123")} className="text-[11px] px-2 py-1.5 rounded-md border hover:bg-bk-lightGreen">Reception</button>
                  <button type="button" onClick={() => quickFill("amit@bkspinecare.in", "Physio@123")} className="text-[11px] px-2 py-1.5 rounded-md border hover:bg-bk-lightGreen">Physio</button>
                </div>
              </div>
            </form>
          )}

          {view === "forgot" && (
            <form onSubmit={doForgot} className="space-y-4">
              <button type="button" onClick={() => setView("login")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" /> Back</button>
              <h1 className="font-display font-extrabold text-2xl text-slate-800">Forgot password</h1>
              <p className="text-sm text-slate-500">Enter your email to generate a reset token.</p>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" data-testid="forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-bk-green hover:bg-bk-greenDark">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset token"}</Button>
            </form>
          )}

          {view === "reset" && (
            <form onSubmit={doReset} className="space-y-4">
              <button type="button" onClick={() => setView("login")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" /> Back</button>
              <h1 className="font-display font-extrabold text-2xl text-slate-800">Reset password</h1>
              <p className="text-sm text-slate-500">Paste the reset token and set a new password.</p>
              <div className="space-y-1.5">
                <Label>Reset token</Label>
                <Input value={token} data-testid="reset-token" onChange={(e) => setToken(e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label>New password</Label>
                <Input type="password" data-testid="reset-password" value={newPass} onChange={(e) => setNewPass(e.target.value)} required />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-bk-green hover:bg-bk-greenDark">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}</Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
